import React, { useState, useEffect, useMemo, useRef } from 'react';
import { TagSelect } from '@Shared.Angular/flowingly.components/TagSelect/TagSelect';
import { Guid } from '@Shared.Angular/@types/guid';
import { getService } from '@Shared.Angular/flowingly.hooks/useService';
import ITagGroupDetail from '@Shared.Angular/@types/core/contracts/queryModel/tagGroup/tagGroupDetail';

type Props = {
  usedTagIds: Guid[];
  initialSelectedTagIds?: Guid[];
  onApply: (selectedTags: Guid[]) => void;
  tagLabel?: string;
  popoverAlign?: 'left' | 'right';
};

export const TagFilter = ({
  usedTagIds,
  initialSelectedTagIds = [],
  onApply,
  tagLabel = 'Find Tags',
  popoverAlign = 'right'
}: Props) => {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [selectedTags, setSelectedTags] = useState<Guid[]>(
    initialSelectedTagIds
  );
  const [tempSelectedTags, setTempSelectedTags] = useState<Guid[]>([]);
  const [allTagGroups, setAllTagGroups] = useState<ITagGroupDetail[]>([]);
  const [usedTagGroups, setUsedTagGroups] = useState<ITagGroupDetail[]>([]);
  const triggerRef = useRef<HTMLDivElement>(null);

  const { enableTagging } = getService('APP_CONFIG');
  const tagGroupApiService = getService('tagGroupApiService');
  const pubsubService = getService('pubsubService');
  const subscriberId = 'tagFilterComponent';

  const usedTagsSet = useMemo(() => new Set(usedTagIds), [usedTagIds]);

  useEffect(() => {
    setSelectedTags(getValidTags(initialSelectedTagIds, usedTagsSet));

    getTagGroups();
    pubsubService.subscribeLate(
      'TAG_GROUP_DELETED',
      getTagGroups,
      subscriberId
    );
    pubsubService.subscribeLate(
      'TAG_GROUP_CREATED',
      getTagGroups,
      subscriberId
    );
    pubsubService.subscribeLate(
      'TAG_GROUP_UPDATED',
      getTagGroups,
      subscriberId
    );
    return () => {
      pubsubService.unsubscribeAll(subscriberId);
    };
  }, []);

  const getTagGroups = () => {
    tagGroupApiService.getAllTagGroups().then((newTagGroups) => {
      setAllTagGroups(newTagGroups);
    });
  };

  useEffect(() => {
    const filteredTagGroups = allTagGroups
      .map((group) => ({
        ...group,
        tags: group.tags.filter((tag) => usedTagsSet.has(tag.id))
      }))
      .filter((group) => group.tags.length > 0);

    setUsedTagGroups(filteredTagGroups);
    resetSelectedTags();
  }, [allTagGroups, usedTagsSet]);

  useEffect(() => {
    const validSelectedTags = getValidTags(selectedTags, usedTagsSet);
    if (validSelectedTags.length !== selectedTags.length) {
      setSelectedTags(validSelectedTags);
      onApply(validSelectedTags);
    }
  }, [usedTagIds]);

  const resetSelectedTags = () => {
    if (usedTagsSet.size === 0) {
      setSelectedTags([]);
    }
  };

  const togglePopover = () => {
    setPopoverOpen((previousPopoverOpen: boolean) => !previousPopoverOpen);
    setTempSelectedTags(getValidTags(selectedTags, usedTagsSet));
  };

  const clearFilter = (event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedTags([]);
    onApply([]);
    setPopoverOpen(false);
  };

  const getValidTags = (tags: Guid[], validTagSet: Set<Guid>): Guid[] =>
    tags.filter((tagId) => validTagSet.has(tagId));

  const closePopover = () => {
    setPopoverOpen(false);
  };

  const applySelectedTags = () => {
    setSelectedTags(tempSelectedTags);
    onApply(tempSelectedTags);
    setPopoverOpen(false);
  };

  const handlePopoverClickOutside = (e: MouseEvent) => {
    if (
      triggerRef.current &&
      !triggerRef.current.contains(e.target as Node) &&
      !document.getElementById('tag-filter-popover')?.contains(e.target as Node)
    ) {
      setPopoverOpen(false);
    }
  };

  useEffect(() => {
    popoverOpen
      ? document.addEventListener('mousedown', handlePopoverClickOutside)
      : document.removeEventListener('mousedown', handlePopoverClickOutside);
    return () => {
      document.removeEventListener('mousedown', handlePopoverClickOutside);
    };
  }, [popoverOpen]);

  const count = selectedTags.length;
  const label = count > 0 ? 'Selected' : tagLabel;

  if (!enableTagging) return null;

  return (
    <div className="tag-filter-wrapper">
      <div
        ref={triggerRef}
        className="tag-filter-trigger"
        onClick={() => togglePopover()}
      >
        {count > 0 && <span className="tag-count">{count}</span>}
        <span className="tag-label">{label}</span>
        {selectedTags.length > 0 && (
          <i
            className="fa-light fa-times"
            title="Clear"
            onClick={clearFilter}
          />
        )}
        <i className="fa-light fa-chevron-down" />
      </div>

      {popoverOpen && (
        <div
          id="tag-filter-popover"
          data-testid="tag-filter-popover"
          className={`tag-filter-popover-container ${
            popoverAlign === 'right' ? 'align-right' : 'align-left'
          }`}
        >
          <TagSelect
            allTagGroups={usedTagGroups}
            selectedTagIds={tempSelectedTags}
            onChange={(newTags: Guid[]) => setTempSelectedTags(newTags)}
          />

          <div className="tag-filter-actions">
            <button className="btn cancel-button" onClick={closePopover}>
              Cancel
            </button>
            <button className="btn apply-button" onClick={applySelectedTags}>
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
